/**
 * Fraud Detection API Client
 * 
 * Phase 9.0: Fraud detection dashboard and player management
 */

import { getShopifySessionToken, getCachedHost, getAppBridgeInstance } from "../utils/app-bridge";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

async function waitForAppBridge(maxWaitMs = 5000): Promise<void> {
  const startTime = Date.now();
  while (!getAppBridgeInstance()) {
    if (Date.now() - startTime > maxWaitMs) {
      throw new Error("Timeout waiting for App Bridge initialization");
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  await waitForAppBridge();
  const sessionToken = await getShopifySessionToken();
  const hostParam = getCachedHost();
  
  let fullEndpoint = `${API_BASE_URL}${endpoint}`;
  if (hostParam) {
    const separator = endpoint.includes("?") ? "&" : "?";
    fullEndpoint += `${separator}host=${encodeURIComponent(hostParam)}`;
  }

  const response = await fetch(fullEndpoint, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || error.message || "API request failed");
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// ============================================================================
// Types
// ============================================================================

export interface FraudFlag {
  id: string;
  shopId: string;
  playerId: string | null;
  type: string;
  severity: number;
  meta: any;
  createdAt: string;
  player?: {
    id: string;
    fingerprint: string;
    customerId: string | null;
    isBlocked: boolean;
  };
}

export interface FraudStats {
  totalFlags: number;
  flagsByType: { type: string; count: number }[];
  flagsBySeverity: { severity: number; count: number }[];
  recentFlags: FraudFlag[];
  blockedPlayers: number;
  flagsLast24h: number;
  flagsLast7d: number;
}

export interface FraudFlagsFilter {
  type?: string;
  severity?: number;
  playerId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface BlockedPlayer {
  id: string;
  fingerprint: string;
  customerId: string | null;
  isBlocked: boolean;
  createdAt: string;
  _count: {
    sessions: number;
    fraudFlags: number;
  };
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get fraud detection statistics
 */
export async function getFraudStats(): Promise<FraudStats> {
  return apiRequest<FraudStats>("/admin/fraud/stats");
}

/**
 * Get fraud flags with filters
 */
export async function getFraudFlags(filter: FraudFlagsFilter = {}): Promise<{ flags: FraudFlag[]; total: number }> {
  const params = new URLSearchParams();
  if (filter.type) params.append("type", filter.type);
  if (filter.severity !== undefined) params.append("severity", filter.severity.toString());
  if (filter.playerId) params.append("playerId", filter.playerId);
  if (filter.dateFrom) params.append("dateFrom", filter.dateFrom);
  if (filter.dateTo) params.append("dateTo", filter.dateTo);
  if (filter.limit) params.append("limit", filter.limit.toString());
  if (filter.offset) params.append("offset", filter.offset.toString());

  const query = params.toString();
  return apiRequest<{ flags: FraudFlag[]; total: number }>(
    `/admin/fraud/flags${query ? `?${query}` : ""}`
  );
}

/**
 * Get fraud history for a specific player
 */
export async function getPlayerFraudHistory(playerId: string): Promise<{ flags: FraudFlag[] }> {
  return apiRequest<{ flags: FraudFlag[] }>(`/admin/fraud/players/${playerId}`);
}

/**
 * Block a player
 */
export async function blockPlayer(playerId: string, reason?: string): Promise<{ success: boolean; message: string }> {
  return apiRequest<{ success: boolean; message: string }>(
    `/admin/fraud/players/${playerId}/block`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    }
  );
}

/**
 * Unblock a player
 */
export async function unblockPlayer(playerId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest<{ success: boolean; message: string }>(
    `/admin/fraud/players/${playerId}/unblock`,
    {
      method: "POST",
    }
  );
}

/**
 * Get list of blocked players
 */
export async function getBlockedPlayers(): Promise<{ players: BlockedPlayer[] }> {
  return apiRequest<{ players: BlockedPlayer[] }>("/admin/fraud/blocked-players");
}
